import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { maintenanceApi } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import type { WorkOrder, WorkOrderFields, WorkOrderKind } from '@/api/types';
import type { TicketPriority } from '@/api/endpoints';
import { Plus } from 'lucide-react';
import { usePermission } from '@/auth/AuthProvider';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/design-system/Button';
import { InlineError } from '@/design-system/Feedback';
import { Checkbox, Select, TextArea, TextInput } from '@/design-system/Inputs';
import { ConfirmDialog, Dialog } from '@/design-system/Overlay';
import { QuickVendorDialog } from '@/features/assets/QuickCatalogDialogs';
import {
  AssetPicker,
  PRIORITY_OPTIONS,
  WORK_ORDER_KIND,
  optionsOf,
  useVendors,
} from './MaintenanceUi';

/** Every cache a work-order change can affect: the lists, the asset's tab, and the dashboards. */
export function useRefreshMaintenance() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
  };
}

function reviewMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 0 || error.status >= 500) return 'The response was not received. This action may already have saved. Close and reopen after refreshing the record to review its current state before trying again.';
  if (error.status === 409) return 'The record changed or a lifecycle prerequisite prevented this action. Close and reopen after refreshing the record before trying again.';
  return null;
}

/** `datetime-local` value for an ISO instant, and back. */
function toLocalInput(iso?: string) {
  if (!iso) return '';
  const local = new Date(iso);
  local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
  return local.toISOString().slice(0, 16);
}
const fromLocalInput = (value: string) => (value ? new Date(value).toISOString() : undefined);
const nowLocalInput = () => toLocalInput(new Date().toISOString());

/**
 * Raising a work order, or changing one that is still open.
 *
 * The contract is left for the server to choose when raising - whichever warranty or AMC covers
 * the asset today - so nobody has to look it up before logging a repair.
 */
export function WorkOrderDialog({
  open,
  onClose,
  order,
  assetId,
  ticketId,
  defaultKind = 'BREAKDOWN',
  defaultTitle = '',
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** Present when editing. */
  order?: WorkOrder;
  assetId?: string;
  ticketId?: string;
  defaultKind?: WorkOrderKind;
  defaultTitle?: string;
  onSaved?: (order: WorkOrder) => void;
}) {
  const refresh = useRefreshMaintenance();
  const canEditCatalogue = usePermission(PERMISSIONS.CATALOG_WRITE);
  const [addingVendor, setAddingVendor] = useState(false);
  const vendors = useVendors();
  const editing = Boolean(order);
  const [assets, setAssets] = useState<string[]>(assetId ? [assetId] : []);
  const [kind, setKind] = useState<WorkOrderKind>(defaultKind);
  const [title, setTitle] = useState(order?.title ?? defaultTitle);
  const [description, setDescription] = useState(order?.description ?? '');
  const [priority, setPriority] = useState<TicketPriority>(order?.priority ?? 'MEDIUM');
  const [dueOn, setDueOn] = useState(order?.dueOn ?? '');
  const [technician, setTechnician] = useState(order?.technician ?? '');
  const [vendorId, setVendorId] = useState(order?.vendorId ?? '');
  const [assetDown, setAssetDown] = useState(false);
  const [downSince, setDownSince] = useState(nowLocalInput());

  const save = useMutation({
    mutationFn: () => {
      const details: WorkOrderFields = {
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        dueOn: dueOn || undefined,
        technician: technician.trim() || undefined,
        vendorId: vendorId || undefined,
        contractId: order?.contractId,
      };
      return order?.id
        ? maintenanceApi.update(order.id, details)
        : maintenanceApi.raise({
            assetId: assets[0],
            kind,
            details,
            ticketId,
            downtimeStartedAt: assetDown ? fromLocalInput(downSince) : undefined,
          });
    },
    onSuccess: (saved) => {
      refresh();
      toast.success(editing ? 'Work order updated' : `Work order ${saved.reference} raised`);
      onSaved?.(saved);
      onClose();
    },
    onError: error => { if (reviewMessage(error)) refresh(); },
  });

  // With a ticket named, the server can take the asset from the ticket.
  const blocked = Boolean(reviewMessage(save.error)) || title.trim() === '' || (!editing && !ticketId && assets.length === 0);

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={editing ? `Edit ${order?.reference}` : 'Raise a work order'}
      description={
        editing
          ? undefined
          : 'A covered asset is linked to its warranty or AMC automatically.'
      }
      busy={save.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={blocked} onClick={() => save.mutate()}>
            {editing ? 'Save changes' : 'Raise work order'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {save.error ? <InlineError error={save.error} /> : null}
        {reviewMessage(save.error) && <p role="alert">{reviewMessage(save.error)}</p>}
        {!editing && kind === 'BREAKDOWN' && <p className="text-small text-text-muted">A breakdown order puts this asset under repair. Its original state and employee custody are preserved; only the last outstanding breakdown completion or cancellation can restore that state, subject to current prerequisites.</p>}

        {!editing && !assetId && (
          <AssetPicker
            value={assets}
            onChange={setAssets}
            required={!ticketId}
            label={ticketId ? 'Asset (the ticket’s asset when left empty)' : 'Asset'}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          {!editing && (
            <Select
              label="Kind"
              value={kind}
              onChange={(event) => setKind(event.target.value as WorkOrderKind)}
              options={optionsOf(WORK_ORDER_KIND)}
            />
          )}
          <Select
            label="Priority"
            value={priority}
            onChange={(event) => setPriority(event.target.value as TicketPriority)}
            options={PRIORITY_OPTIONS}
          />
        </div>

        <TextInput label="Title" required maxLength={200} value={title}
          placeholder="e.g. Laptop fan noisy, replace fan"
          onChange={(event) => setTitle(event.target.value)} />
        <TextArea label="Description" rows={3} maxLength={2000} value={description}
          onChange={(event) => setDescription(event.target.value)} />

        <div className="grid gap-4 sm:grid-cols-3">
          <TextInput label="Due by" type="date" value={dueOn}
            onChange={(event) => setDueOn(event.target.value)} />
          <TextInput label="Technician" maxLength={160} value={technician}
            placeholder="Name of whoever does the work"
            onChange={(event) => setTechnician(event.target.value)} />
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Select label="Vendor" value={vendorId} options={vendors.optionsFor(vendorId)} disabled={!vendors.canRead}
                onChange={(event) => setVendorId(event.target.value)} />
            </div>
            {canEditCatalogue ? (
              <Button
                variant="secondary"
                aria-label="Add a vendor"
                title="Add a vendor"
                onClick={() => setAddingVendor(true)}
                className="mb-px h-9 shrink-0 px-3"
              >
                <Plus className="size-4" />
              </Button>
            ) : null}
          </div>
        </div>

        {!editing && (
          <div className="space-y-3 rounded-lg border border-border-subtle p-3">
            <Checkbox
              label="The asset is out of use now"
              description="Starts the downtime clock; it stops when the work is completed."
              checked={assetDown}
              onChange={(event) => setAssetDown(event.target.checked)}
            />
            {assetDown && (
              <TextInput label="Out of use since" type="datetime-local" value={downSince}
                onChange={(event) => setDownSince(event.target.value)} />
            )}
          </div>
        )}
      </div>
    </Dialog>

    <QuickVendorDialog
      open={addingVendor}
      onClose={() => setAddingVendor(false)}
      onCreated={(vendor) => {
        setAddingVendor(false);
        setVendorId(vendor.id);
      }}
    />
    </>
  );
}

/** Closing a work order with what was done, what it cost and, if needed, corrected downtime. */
export function CompleteWorkOrderDialog({
  order,
  onClose,
}: {
  order: WorkOrder | null;
  onClose: () => void;
}) {
  const refresh = useRefreshMaintenance();
  const [resolution, setResolution] = useState('');
  const [labourCost, setLabourCost] = useState('');
  const [partsCost, setPartsCost] = useState('');
  const [currencyCode, setCurrencyCode] = useState(order?.currencyCode ?? 'INR');
  const [downFrom, setDownFrom] = useState(toLocalInput(order?.downtimeStartedAt));
  const [downUntil, setDownUntil] = useState(toLocalInput(order?.downtimeEndedAt));

  const complete = useMutation({
    mutationFn: () => {
      const costed = labourCost !== '' || partsCost !== '';
      return maintenanceApi.complete(order!.id!, {
        resolution: resolution.trim(),
        labourCost: labourCost === '' ? undefined : Number(labourCost),
        partsCost: partsCost === '' ? undefined : Number(partsCost),
        currencyCode: costed ? currencyCode.trim().toUpperCase() : undefined,
        downtimeStartedAt: fromLocalInput(downFrom),
        downtimeEndedAt: fromLocalInput(downUntil),
      });
    },
    onSuccess: (saved) => {
      refresh();
      toast.success(`${saved.reference} completed`);
      onClose();
    },
    onError: error => { if (reviewMessage(error)) refresh(); },
  });

  return (
    <Dialog
      open={Boolean(order)}
      onClose={onClose}
      title={`Complete ${order?.reference ?? ''}`}
      description={
        order?.scheduleId
          ? 'The schedule’s next occurrence will be counted from today.'
          : order?.kind === 'BREAKDOWN' ? 'When a repair context exists, only the last outstanding breakdown order restores its original asset state; employee custody stays unchanged. Historical orders without a repair context leave asset state unchanged. If current prerequisites fail, completion is refused and the order stays open. Downtime still running ends now unless an end is recorded below.' : 'Downtime still running ends now unless you set an end below.'
      }
      busy={complete.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={complete.isPending}>
            Back
          </Button>
          <Button variant="primary" loading={complete.isPending}
            disabled={resolution.trim() === '' || Boolean(reviewMessage(complete.error))} onClick={() => complete.mutate()}>
            Complete work order
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {complete.error ? <InlineError error={complete.error} /> : null}
        {reviewMessage(complete.error) && <p role="alert">{reviewMessage(complete.error)}</p>}
        <TextArea label="What was done" required rows={3} maxLength={2000} value={resolution}
          onChange={(event) => setResolution(event.target.value)} />
        <div className="grid gap-4 sm:grid-cols-3">
          <TextInput label="Labour cost" type="number" min={0} step="0.01" value={labourCost}
            onChange={(event) => setLabourCost(event.target.value)} />
          <TextInput label="Parts cost" type="number" min={0} step="0.01" value={partsCost}
            onChange={(event) => setPartsCost(event.target.value)} />
          <TextInput label="Currency" maxLength={3} value={currencyCode}
            onChange={(event) => setCurrencyCode(event.target.value)} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput label="Out of use from" type="datetime-local" value={downFrom}
            onChange={(event) => setDownFrom(event.target.value)} />
          <TextInput label="Back in use at" type="datetime-local" value={downUntil}
            onChange={(event) => setDownUntil(event.target.value)} />
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Asking for a reason before something is cancelled. Shared by work orders and contracts: both
 * keep the reason in the audit trail.
 */
export function ReasonDialog({
  open,
  title,
  message,
  confirmLabel,
  successMessage,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  successMessage: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<unknown>;
}) {
  const refresh = useRefreshMaintenance();
  const [reason, setReason] = useState('');
  const run = useMutation({
    mutationFn: () => onConfirm(reason.trim()),
    onSuccess: () => {
      refresh();
      toast.success(successMessage);
      setReason('');
      onClose();
    },
    onError: error => { if (reviewMessage(error)) refresh(); },
  });

  useEffect(() => { if (open) run.reset(); }, [open]);

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={() => run.mutate()}
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      destructive
      loading={run.isPending}
      confirmDisabled={reason.trim() === '' || Boolean(reviewMessage(run.error))}
    >
      <div className="space-y-3">
        {run.error ? <InlineError error={run.error} /> : null}
        {reviewMessage(run.error) && <p role="alert">{reviewMessage(run.error)}</p>}
        <TextArea label="Reason" required rows={3} maxLength={500} value={reason}
          description="Recorded in the audit log."
          onChange={(event) => setReason(event.target.value)} />
      </div>
    </ConfirmDialog>
  );
}

